import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { ContentActionsMenu, EditedLabel } from "../components/ContentActionsMenu";
import {
  DollarSign, TrendingUp, MapPin, BarChart3, Plus,
  Briefcase, Shield, ChevronDown, Users
} from "lucide-react";

const POSITION_LABELS = {
  permanent_full_time: "Permanent Full-Time", permanent_part_time: "Permanent Part-Time",
  temporary: "Temporary", contract: "Contract", casual: "Casual"
};
const EXP_RANGES = ["0-1", "2-4", "5-9", "10-14", "15-19", "20+"];
const SETTINGS = ["General Practice", "Periodontics", "Orthodontics", "Public Health", "Mobile/Travel", "Corporate", "Other"];

const WageTransparencyPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterProvince, setFilterProvince] = useState("");
  const [filterType, setFilterType] = useState("");
  const EMPTY_FORM = {
    province: "AB", position_type: "permanent_full_time", hourly_rate: "",
    years_experience: "2-4", has_benefits: false, benefits_details: "",
    other_incentives: "", work_setting: ""
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const setFormDialog = (open) => {
    setShowAdd(open);
    if (!open) { setEditingId(null); setForm(EMPTY_FORM); }
  };

  const startEdit = (e) => {
    setEditingId(e.wage_id);
    setForm({
      province: e.province, position_type: e.position_type, hourly_rate: String(e.hourly_rate),
      years_experience: e.years_experience, has_benefits: !!e.has_benefits, benefits_details: e.benefits_details || "",
      other_incentives: e.other_incentives || "", work_setting: e.work_setting || "",
    });
    setShowAdd(true);
  };

  useEffect(() => {
    fetchAll();
    axios.get(`${API}/provinces`).then(r => setProvinces(r.data)).catch(() => {});
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterProvince) params.append("province", filterProvince);
      if (filterType) params.append("position_type", filterType);
      const [statsRes, entriesRes, myRes] = await Promise.all([
        axios.get(`${API}/wages/stats${filterProvince ? `?province=${filterProvince}` : ""}`),
        axios.get(`${API}/wages/entries?${params}`),
        axios.get(`${API}/wages/my-entries`)
      ]);
      setStats(statsRes.data);
      setEntries(entriesRes.data);
      setMyEntries(myRes.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filterProvince, filterType]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.hourly_rate) { toast.error("Enter your hourly rate"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, hourly_rate: parseFloat(form.hourly_rate) };
      if (editingId) {
        await axios.put(`${API}/wages/entries/${editingId}`, payload);
        toast.success("Entry updated — still anonymous");
      } else {
        await axios.post(`${API}/wages/entries`, payload);
        toast.success("Entry added anonymously!");
      }
      setFormDialog(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || (editingId ? "Failed to update" : "Failed to add")); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/wages/entries/${id}`); toast.success("Entry deleted — averages updated"); fetchAll(); }
    catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  const wageMenu = (e, scope) => (
    <ContentActionsMenu
      testIdPrefix={`wage-${scope}-${e.wage_id}`}
      canManage={e.can_manage}
      reportType="wage_entry"
      reportId={e.wage_id}
      onEdit={() => startEdit(e)}
      onDelete={() => handleDelete(e.wage_id)}
      deleteTitle="Delete this wage entry?"
      deleteDescription="This permanently removes the entry and recalculates all wage averages. This cannot be undone."
    />
  );

  const fmt = (v) => v ? `$${Number(v).toFixed(2)}` : "—";

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="wage-transparency-page">
        <div className="bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="wage-disclaimer">
          <Shield className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            Wage data is self-reported by members and is provided for informational purposes only. DHDH does not guarantee the accuracy of survey results.
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <EditableText
              blockKey="wages.title"
              fallback="Wage Transparency"
              as="h1"
              className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="wages.subtitle"
              fallback="See how dental hygienists are compensated across Canada"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <Dialog open={showAdd} onOpenChange={setFormDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" data-testid="add-wage-btn"><Plus className="w-4 h-4 mr-2" /> Share Your Wage</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingId ? "Edit Wage Entry" : "Share Your Compensation"}</DialogTitle></DialogHeader>
              <p className="text-xs text-[#64748B] mt-1">{editingId ? "Edits stay completely anonymous — no name is ever attached." : "Your entry is completely anonymous."}</p>
              <form onSubmit={handleAdd} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Province</Label>
                    <Select value={form.province} onValueChange={v => setForm({...form, province: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                      <SelectContent>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label>Hourly Rate (CAD) *</Label><Input type="number" step="0.01" value={form.hourly_rate} onChange={e => setForm({...form, hourly_rate: e.target.value})} placeholder="0.00" className="mt-2 bg-white border-[#E5DFEC]" data-testid="wage-rate-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Position Type</Label>
                    <Select value={form.position_type} onValueChange={v => setForm({...form, position_type: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(POSITION_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label>Years Experience</Label>
                    <Select value={form.years_experience} onValueChange={v => setForm({...form, years_experience: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger>
                      <SelectContent>{EXP_RANGES.map(r => <SelectItem key={r} value={r}>{r} years</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div><Label>Work Setting</Label>
                  <Select value={form.work_setting || "none"} onValueChange={v => setForm({...form, work_setting: v === "none" ? "" : v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select...</SelectItem>{SETTINGS.map(s => <SelectItem key={s} value={s.toLowerCase()}>{s}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="benefits" checked={form.has_benefits} onCheckedChange={c => setForm({...form, has_benefits: c})} />
                  <Label htmlFor="benefits" className="cursor-pointer text-sm">Benefits included</Label>
                </div>
                {form.has_benefits && <div><Label>Benefits Details</Label><Input value={form.benefits_details} onChange={e => setForm({...form, benefits_details: e.target.value})} placeholder="e.g. Health, dental, vision, RRSP matching" className="mt-2 bg-white border-[#E5DFEC]" /></div>}
                <div><Label>Other Incentives</Label><Input value={form.other_incentives} onChange={e => setForm({...form, other_incentives: e.target.value})} placeholder="e.g. CE allowance, uniform stipend, bonuses" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setFormDialog(false)} className="rounded-full">Cancel</Button>
                  <Button type="submit" className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" disabled={submitting} data-testid="submit-wage-btn">{submitting ? "Saving..." : editingId ? "Save changes" : "Submit Anonymously"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <Select value={filterProvince || "all"} onValueChange={v => setFilterProvince(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-white border-[#E5DFEC]"><SelectValue placeholder="Province" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Provinces</SelectItem>{provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterType || "all"} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 bg-white border-[#E5DFEC]"><SelectValue placeholder="Position Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{Object.entries(POSITION_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-28 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : (
          <>
            {/* Overview Stats */}
            {stats?.overall?.total_entries > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gradient-to-br from-[#10B981] to-[#059669] text-white rounded-2xl p-5">
                  <DollarSign className="w-5 h-5 mb-1 opacity-70" />
                  <p className="text-2xl font-medium">{fmt(stats.overall.avg_rate)}</p>
                  <p className="text-xs text-white/60">Avg Hourly</p>
                </div>
                <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                  <TrendingUp className="w-5 h-5 mb-1 text-[#8DB5A0]" />
                  <p className="text-2xl font-medium text-[#6B4E8A]">{fmt(stats.overall.max_rate)}</p>
                  <p className="text-xs text-[#64748B]">Highest</p>
                </div>
                <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                  <DollarSign className="w-5 h-5 mb-1 text-[#8DB5A0]" />
                  <p className="text-2xl font-medium text-[#6B4E8A]">{fmt(stats.overall.min_rate)}</p>
                  <p className="text-xs text-[#64748B]">Lowest</p>
                </div>
                <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
                  <Users className="w-5 h-5 mb-1 text-[#3B82F6]" />
                  <p className="text-2xl font-medium text-[#6B4E8A]">{stats.overall.total_entries}</p>
                  <p className="text-xs text-[#64748B]">Entries ({Math.round(stats.overall.with_benefits / stats.overall.total_entries * 100) || 0}% w/ benefits)</p>
                </div>
              </motion.div>
            )}

            {/* By Province */}
            {stats?.by_province?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 mb-8">
                <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>By Province</h3>
                <div className="space-y-3">
                  {stats.by_province.map(p => {
                    const maxRate = stats.by_province[0]?.avg_rate || 1;
                    const pct = (p.avg_rate / maxRate) * 100;
                    return (
                      <div key={p.province}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#6B4E8A] font-medium">{p.province}</span>
                          <span className="text-[#64748B]">{fmt(p.avg_rate)} avg ({p.count} entries)</span>
                        </div>
                        <div className="w-full bg-[#F0ECF7] rounded-full h-2.5">
                          <div className="h-2.5 rounded-full bg-[#10B981] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Individual Entries */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6">
              <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Anonymous Entries ({entries.length})</h3>
              {entries.length > 0 ? (
                <div className="space-y-2">
                  {entries.map(e => (
                    <div key={e.wage_id} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-xl">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-2 h-10 rounded-full bg-[#10B981] shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#6B4E8A]">{fmt(e.hourly_rate)}/hr</span>
                            <span className="text-xs px-2 py-0.5 bg-[#F0ECF7] rounded-full">{e.province}</span>
                            <span className="text-xs px-2 py-0.5 bg-[#8DB5A0]/10 text-[#8DB5A0] rounded-full">{POSITION_LABELS[e.position_type] || e.position_type}</span>
                          </div>
                          <p className="text-xs text-[#64748B] mt-0.5">
                            {e.years_experience} yrs exp{e.has_benefits ? " · Benefits" : ""}{e.work_setting ? ` · ${e.work_setting}` : ""}{e.other_incentives ? ` · ${e.other_incentives}` : ""}
                          </p>
                          <EditedLabel editedAt={e.edited_at} testId={`wage-entry-${e.wage_id}-edited`} />
                        </div>
                      </div>
                      {wageMenu(e, "entry")}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No entries yet. Be the first to share!</p>
                </div>
              )}
            </motion.div>

            {/* My Entries */}
            {myEntries.length > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 bg-white border border-[#E5DFEC] rounded-2xl p-6">
                <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>My Entries</h3>
                <div className="space-y-2">
                  {myEntries.map(e => (
                    <div key={e.wage_id} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-xl">
                      <span className="text-sm">{fmt(e.hourly_rate)}/hr · {e.province} · {POSITION_LABELS[e.position_type] || e.position_type} <EditedLabel editedAt={e.edited_at} testId={`wage-mine-${e.wage_id}-edited`} /></span>
                      {wageMenu(e, "mine")}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default WageTransparencyPage;
